import { useLearnerCoaching } from '@/hooks/useLearnerCoaching'
import { useOrgGroupAccess } from '@/hooks/useOrgGroupAccess'
import { useAssessmentsAccess } from '@/hooks/feature-access/useAssessmentsAccess'
import { FeatureAccessPath } from '@/types/configuration'
import { ClerkUserRole } from '@/types/auth'
import { useFlag } from '@unleash/proxy-client-react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

export type NavLink = {
  label: string
  path: string
  submenu?: {
    label: string
    path: string
    description?: string
  }[]
}

export function useHeaderNavigation(): {
  navLinks: NavLink[]
  isLinkActive: (link: NavLink) => boolean
  isSubLinkActive: (subLink: { path: string }) => boolean
} {
  const location = useLocation()
  const { t } = useTranslation()

  // Feature flags (Unleash - global on/off)
  const writingHelperEnabled = useFlag('writing-helper')
  const progressTrackingEnabled = useFlag('progress-tracking')
  const wordBankEnabled = useFlag('word-bank-access')
  const showLearningTime = useFlag('show-learning-time')
  const { coachingEnabled } = useLearnerCoaching()

  // Org/Group access (Backend configuration - per-tenant control)
  const { hasAccess: groupClassesEnabled } = useOrgGroupAccess({
    configPath: FeatureAccessPath.LIVE_SESSIONS_GROUP_CLASSES_ENABLED,
    restrictedRoles: [ClerkUserRole.LEARNER, ClerkUserRole.COACH],
  })
  const { hasAccess: privateClassesEnabled } = useOrgGroupAccess({
    configPath: FeatureAccessPath.LIVE_SESSIONS_PRIVATE_CLASSES_ENABLED,
    restrictedRoles: [ClerkUserRole.LEARNER],
  })

  // Show Live Instruction menu if either group or private classes is enabled
  const liveInstructionEnabled = groupClassesEnabled || privateClassesEnabled

  // Assessments access (combines feature flag + org/group config)
  const { isEnabled: learnerAssessmentsFullyEnabled } = useAssessmentsAccess({ context: 'learner' })

  // Build navigation links based on feature flags
  const navLinks: NavLink[] = [
    { label: t('header.home'), path: '/' },
    {
      label: t('header.catalog'),
      path: '/courses',
      submenu: [
        {
          label: t('header.courses'),
          path: '/courses',
          description: t('header.coursesDescription'),
        },
        {
          label: t('header.lessons'),
          path: '/lessons',
          description: t('header.lessonsDescription'),
        },
      ],
    },
    {
      label: t('header.practice'),
      path: '/grammar',
      submenu: [
        ...(writingHelperEnabled
          ? [
              {
                label: t('header.writingHelper'),
                path: '/writing',
                description: t('header.writingHelperDescription'),
              },
            ]
          : []),
        ...(wordBankEnabled
          ? [
              {
                label: t('header.wordBank'),
                path: '/word-bank',
                description: t('header.wordBankDescription'),
              },
            ]
          : []),
        {
          label: t('header.grammarGuide'),
          path: '/grammar',
          description: t('header.grammarGuideDescription'),
        },
      ],
    },
    ...(liveInstructionEnabled
      ? [
          {
            label: t('header.liveInstruction'),
            path: groupClassesEnabled ? '/group-classes' : '/private-classes',
            submenu: [
              ...(groupClassesEnabled
                ? [
                    {
                      label: t('header.groupClasses'),
                      path: '/group-classes',
                      description: t('header.groupClassesDescription'),
                    },
                  ]
                : []),
              ...(privateClassesEnabled
                ? [
                    {
                      label: t('header.privateClasses'),
                      path: '/private-classes',
                      description: t('header.privateClassesDescription'),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    // One entry with no submenu (ENG-3750): the coaching home reaches the coach list and messages.
    ...(coachingEnabled ? [{ label: 'Coaching', path: '/coaching' }] : []),
    ...(progressTrackingEnabled || learnerAssessmentsFullyEnabled
      ? [
          {
            label: t('header.performance'),
            path: learnerAssessmentsFullyEnabled ? '/performance' : '/history',
            submenu: [
              ...(learnerAssessmentsFullyEnabled
                ? [
                    {
                      label: t('header.assessments'),
                      path: '/performance',
                      description: t('header.assessmentsDescription'),
                    },
                  ]
                : []),
              ...(progressTrackingEnabled
                ? [
                    {
                      label: t('learnerHistory.title'),
                      path: '/history',
                      description: t('learnerHistory.description'),
                    },
                    ...(showLearningTime
                      ? [
                          {
                            label: t('learningTime.menu.label'),
                            path: '/learning-time',
                            description: t('learningTime.menu.description'),
                          },
                        ]
                      : []),
                  ]
                : []),
            ],
          },
        ]
      : []),
  ]

  const isLinkActive = (link: NavLink) =>
    location.pathname === link.path ||
    (!link.submenu && location.pathname.startsWith(`${link.path}/`)) ||
    (link.submenu?.some((sub) => location.pathname === sub.path || location.pathname.startsWith(sub.path + '/')) ??
      false)

  const isSubLinkActive = (subLink: { path: string }) =>
    location.pathname === subLink.path || location.pathname.startsWith(subLink.path + '/')

  return { navLinks, isLinkActive, isSubLinkActive }
}
